import React from 'react'
import moment from 'moment'
import {Link, useNavigate} from 'react-router-dom'
import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import Swal from 'sweetalert2'
import type {Action, ViewType} from '../MesActionsListPage'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'
import {removeAction} from '../utils/helpers'
import {realiserAction} from '../../../../components/requests'

type CardItemProps = {
  action: Action
  currentUser: any
  view: ViewType
  setActions: React.Dispatch<React.SetStateAction<Action[]>>
  filters: any
}

const CardItem = ({action, currentUser, view, setActions, filters}: CardItemProps) => {
  const navigate = useNavigate()

  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: String(action.id),
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
    cursor: view === 'List' ? 'default' : 'grab',
  }

  const marquerFaitAction = (actionId: number | string) => {
    Swal.fire({
      title: 'Attention',
      text: 'Etes-vous sûr de terminer cette action?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#1BC5BD',
      cancelButtonColor: '#F64E60',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const response = await realiserAction(actionId, 'TERMINEE')

          if (response?.status === 200 && response.data) {
            setActions((prev: Action[]) =>
              prev
                .map((item) =>
                  String(item.id) === String(actionId)
                    ? {
                        ...item,
                        ...response.data,
                      }
                    : item
                )
                .filter(
                  (item) => item.statut?.code === 'NOUVELLE' || item.statut?.code === 'ENCOURS'
                )
            )

            return true
          }

          throw new Error('Échec de la réalisation.')
        } catch (error) {
          throw error
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Terminée',
          text: 'L’action a bien été terminée.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        })
      }
    })
  }

  const handleClick = () => {
    if (action?.demande?.id && action?.demande?.client?.id) {
      navigate(`/demandes/${action.demande.client.id}/traitement/${action.demande.id}`, {
        state: {
          idDemande: action.demande.id,
          viaListAction: true
        },
      })
    }
  }

  const openAction = () => {
    navigate(`/mes-actions/${action.id}/modifier/`, {
      state: {
        readOnly: true,
        isRealisation: action?.statut?.code !== 'TERMINEE',
        view: view,
        filters: filters
      },
    })
  }

  const isEcheanceDepassee = (dateEcheance?) => {
    if (!dateEcheance) {
      return false
    }

    const echeance = new Date(dateEcheance)
    const today = new Date()

    echeance.setHours(0, 0, 0, 0)
    today.setHours(0, 0, 0, 0)

    return echeance < today
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        cursor: view === 'List' ? 'default' : 'grab',
      }}
      {...attributes}
      {...listeners}
      className={`${view === 'List' ? 'col-md-4' : 'row'} mb-4 px-2`}
    >
      <div className='card shadow-sm h-100'>
        <div className='card-header fw-bold' title={action?.titre}>
          <div className='col-md-8 my-5' onClick={openAction}>
            <h4>
              {action?.type?.libelle} {' - '}
              {action?.titre && action.titre.length > 70
                ? `${action.titre.substring(0, 70)}…`
                : action?.titre}
            </h4>
          </div>

          <div className='col-md-4 my-5 d-flex align-items-center justify-content-between'>
            {action?.demande && (
              <span
                onClick={handleClick}
                style={{
                  cursor: 'pointer',
                }}
              >
                <i className='badge badge-light me-3'>Demande n°</i>

                <i
                  className='badge badge-info badge-circle'
                  style={{
                    fontStyle: 'normal',
                  }}
                >
                  {action.demande.id}
                </i>
              </span>
            )}

            {action?.savDossier && (
              <Link to={`/sav/dossiers/${action.savDossier.id}`} className='badge badge-light-info me-3'>
                {action.savDossier.numero}
              </Link>
            )}

            {/* MENU */}
            {(shouldDisplayFonctionnalite(currentUser, 'MODIFIER ACTION') ||
              shouldDisplayFonctionnalite(currentUser, 'SUPPRIMER ACTION')) &&
              (action?.statut?.code === 'NOUVELLE') && (
                <div className='dropdown'>
                  <button
                    className='btn btn-sm btn-icon btn-light'
                    type='button'
                    data-bs-toggle='dropdown'
                    aria-expanded='false'
                  >
                    <i className='bi bi-three-dots-vertical'></i>
                  </button>

                  <ul className='dropdown-menu'>
                    {/* MODIFIER */}
                    <li>
                      {shouldDisplayFonctionnalite(currentUser, 'MODIFIER ACTION') && (
                        <Link className='dropdown-item' to={`/mes-actions/${action.id}/modifier`} state={{view: view, filters: filters}}>
                          Modifier
                        </Link>
                      )}
                    </li>
                    <li>
                      {shouldDisplayFonctionnalite(currentUser, 'SUPPRIMER ACTION') && (
                        <button
                          className='dropdown-item text-danger'
                          onClick={() => removeAction(action.id, setActions)}
                        >
                          Supprimer
                        </button>
                      )}
                    </li>
                  </ul>
                </div>
              )}
          </div>
        </div>
        <div className='card-body lh-lg' onClick={openAction}>
          <div className='row'>
            {/* COMMENTAIRE */}
            <div className='col-md-8'>
              Commentaire :
              <br />
              <div
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3,
                  overflow: 'hidden',
                }}
              >
                {action?.commentaire}
              </div>
            </div>

            {/* CLIENT / ASSIGNE */}
            <div className='col-md-4'>
              {/* CLIENT */}
              {(action?.client?.nomSociete || action?.client?.prenom || action?.client?.nom) && (
                <div>
                  <i className='bi bi-link me-2 text-muted'></i>

                  <span>
                    {action?.client?.nomSociete} {action?.client?.prenom} {action?.client?.nom}
                  </span>
                </div>
              )}

              {/* ASSIGNE */}
              <div className='mb-3 d-flex flex-wrap gap-2'>
                <span className='badge badge-white'>
                  <i className='bi bi-person me-2 text-muted'></i>

                  <span>
                    {action?.assigneeA
                      ? action.assigneeA.prenom === currentUser.prenom &&
                        action.assigneeA.nom === currentUser.nom
                        ? 'Moi'
                        : `${action.assigneeA.prenom} ${action.assigneeA.nom}`
                      : ''}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer' onClick={openAction}>
          <div className='row'>
            {/* DATES */}
            <div className='col-md-4'>
              <div>
                Créé : {action?.createdAt ? moment(action.createdAt).format('DD/MM/YYYY') : ''}
              </div>

              <div className='text-primary'>
                Lu le : {action?.dateLecture ? moment(action.dateLecture).format('DD/MM/YYYY') : ''}
              </div>
            </div>

            {/* ORIGINE */}
            <div className='col-md-4'>
              <span
                className={`badge badge-${
                  action?.origine === 'Automatique' ? 'dark' : 'light'
                } ms-2`}
              >
                <i
                  className={`${
                    action?.origine === 'Automatique'
                      ? 'fa fa-clock text-white'
                      : 'bi bi-list-check text-muted'
                  } me-2`}
                ></i>

                {action?.origine}
              </span>

              {action?.prioritaire && (
                <span className='badge badge-danger ms-2'>
                  <i className='bi bi-exclamation-triangle-fill fs-5 me-2 text-white'></i>
                  Prioritaire
                </span>
              )}
            </div>

            {/* STATUT */}
            <div className='col-md-4'>
              <div className='d-flex justify-content-between align-items-center'>
                <span
                  className={`badge rounded-pill shadow-sm ${
                    action?.statut?.code === 'NOUVELLE'
                      ? 'badge-white'
                      : action?.statut?.code === 'ENCOURS'
                        ? 'badge-dark'
                        : action?.statut?.code === 'TERMINEE'
                          ? 'badge-light'
                          : 'badge-secondary'
                  }`}
                >
                  <i
                    className={`${
                      action?.statut?.code === 'NOUVELLE'
                        ? 'fa fa-clock text-muted'
                        : action?.statut?.code === 'ENCOURS'
                          ? 'bi bi-list-check fs-5 me-2 text-white'
                          : action?.statut?.code === 'TERMINEE'
                            ? 'bi bi-check-circle text-muted'
                            : 'bi bi-question-circle'
                    } me-2`}
                  ></i>

                  {action?.statut?.libelle}
                </span>
              </div>

              {/* DEPASSEMENT */}
              {isEcheanceDepassee(action?.dateEcheance) && (
                <div className='text-danger mt-2 fs-7'>Échéance dépassée</div>
              )}

              {/* ECHEANCE */}
              <div className='text-muted'>
                <i className='bi bi-calendar-event me-2 text-muted'></i>

                {action?.dateEcheance
                  ? moment.parseZone(action.dateEcheance).format('DD/MM/YYYY')
                  : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CardItem
